import { useEffect, useState } from 'react';
import { ChevronRightIcon } from '@heroicons/react/16/solid';
import FormInput from 'components/common/form-input';
import { existingSystemComponentTypeProperties as componentTypeProperties } from 'data/constants';
import type { ExistingSystemComponentType } from 'data/types';
import useExistingEquipmentFieldTooltips from 'hooks/use-existing-equipment-field-tooltips';
import { conditionallyRequiredSpecKeys } from 'utils/existing-equipment';
import {
  parseRefrigerantFamilySupport,
  serializeRefrigerantFamilySupport
} from 'utils/refrigerant-family-support';

// Section label for the collapsible advanced-spec group (Geoff ruling,
// AHA-6681 spec-field reorg). Tests build their queries from this constant.
export const ADVANCED_SPECS_SECTION_LABEL = 'Advanced Specifications';

// Shared per-type spec-field renderer (AHA-6681 batch 6), extracted from
// ExistingSystemComponentForm so the add-equipment modal can render the same
// fields against LOCAL (unsaved) form state. Grouping: matching AND required
// specs up front (a required field must never hide behind the collapsed
// toggle — AHA-6796), everything else behind the collapsible
// "Advanced Specifications (N)" toggle. Manufacturer is excluded entirely —
// each caller renders its own
// Manufacturer input beside Model Number (AHA-6681). Returns a fragment so
// callers control the flex-wrap container (keeps both existing layouts
// intact).
export default function ExistingComponentSpecFields({
  componentType,
  values,
  onChange,
  disabled = false,
  getFieldSubLabel
}: {
  componentType: ExistingSystemComponentType['type'] | undefined;
  values: Record<string, any>;
  onChange: (propKey: string, value: any) => void;
  disabled?: boolean;
  getFieldSubLabel?: (propKey: string) => string | undefined;
}) {
  const [showAdvanced, setShowAdvanced] = useState(false);
  // Admin-managed info tooltips keyed by spec key (AHA-6796): any key with
  // content gets an info icon next to its label, in every consumer of this
  // renderer — a future tooltip is one admin/seed row, zero component work.
  const tooltips = useExistingEquipmentFieldTooltips();

  useEffect(() => {
    setShowAdvanced(false);
  }, [componentType]);

  const currentPropertiesConfig =
    (componentType && componentTypeProperties[componentType]) || {};

  // Conditionally required keys (AHA-6796) — the shared union of the
  // furnace rules (same source of truth as the page-level continue gate),
  // live against the current form values so the fields re-partition and
  // the asterisks follow edits:
  // - Combustion-furnace: ventingMaterial becomes required when fuelType
  //   is a combustion fuel (Natural Gas / Propane (LP), legacy 'Propane' /
  //   Oil); with fuelType unset or Electricity it stays optional (electric
  //   furnaces have no flue).
  const conditionallyRequiredKeys = conditionallyRequiredSpecKeys({
    type: componentType,
    fuelType: values.fuelType
  });

  const allEntries = Object.entries(currentPropertiesConfig);
  // Conditionally required keys partition up front too — a required field
  // (static OR conditional) must never hide behind the collapsed toggle.
  const upFrontEntries = allEntries.filter(
    ([key, config]) =>
      config.matching === true ||
      config.required === true ||
      conditionallyRequiredKeys.includes(key)
  );
  const advancedEntries = allEntries.filter(
    ([key, config]) =>
      config.matching !== true &&
      config.required !== true &&
      !conditionallyRequiredKeys.includes(key) &&
      key !== 'manufacturer'
  );

  const renderFormInput = ([propKey, config]: [string, any]) => {
    const rawValue = values[propKey];
    let displayValue = rawValue;
    if (config.isBoolean) {
      if (rawValue === true) displayValue = 'Yes';
      else if (rawValue === false) displayValue = 'No';
    }
    // The multiselect string↔array boundary lives here: spec state stays a
    // plain catalog-format string everywhere (modal specValues, Redux, save
    // payloads, PATCH bodies) and only the input works with arrays.
    if (config.type === 'multiselect') {
      displayValue = parseRefrigerantFamilySupport(rawValue);
    }
    const handleChange = (value: any) => {
      if (config.isBoolean) {
        const boolMap: Record<string, boolean | null> = {
          Yes: true,
          No: false
        };
        onChange(propKey, boolMap[value] ?? null);
      } else if (config.type === 'multiselect') {
        onChange(propKey, serializeRefrigerantFamilySupport(value));
      } else {
        onChange(propKey, value);
      }
    };
    return (
      <FormInput
        key={propKey}
        {...config}
        required={
          config.required === true ||
          conditionallyRequiredKeys.includes(propKey)
        }
        subLabel={getFieldSubLabel?.(propKey)}
        tooltip={tooltips[propKey]}
        tooltipLocation='after_label'
        value={displayValue}
        onChange={handleChange}
        placeholder={config.type === 'number' ? '0' : config.placeholder}
        disabled={disabled}
      />
    );
  };

  if (!componentType || allEntries.length === 0) {
    return null;
  }

  return (
    <>
      {upFrontEntries.map((entry) => renderFormInput(entry))}
      {advancedEntries.length > 0 && (
        <div key='advanced-specifications' className='w-full'>
          <button
            type='button'
            className='flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-800'
            onClick={() => setShowAdvanced(!showAdvanced)}
          >
            <ChevronRightIcon
              className={`h-4 w-4 transition-transform ${
                showAdvanced ? 'rotate-90' : ''
              }`}
            />
            {ADVANCED_SPECS_SECTION_LABEL} ({advancedEntries.length})
          </button>
          {showAdvanced && (
            <div className='mt-2 flex flex-wrap items-center gap-x-5 gap-y-3'>
              {advancedEntries.map((entry) => renderFormInput(entry))}
            </div>
          )}
        </div>
      )}
    </>
  );
}
